import React, { useEffect, useRef, useState } from "react";
import { Send, Sparkles, X, ChevronDown, Reply, Info } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const field = "w-full bg-transparent text-[13.5px] outline-none placeholder:text-muted-foreground/50";

function Line({ label, children, testId }) {
  return (
    <label className="flex min-h-[40px] items-center gap-3 border-b border-black/[0.05] px-5" data-testid={testId}>
      <span className="w-12 shrink-0 text-[12px] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

/** Replies we have sent, newest last. Delivery is recorded in the thread; outbound e-mail is not connected yet. */
export function Thread({ thread }) {
  const [open, setOpen] = useState(null);
  if (!thread?.length) return null;
  return (
    <section className="space-y-3" data-testid="inbox-thread">
      {thread.map((t) => {
        const on = open === t.id || thread.length === 1;
        return (
          <article key={t.id} className="rounded-[18px] border border-[hsl(var(--navy)/0.12)] bg-[hsl(var(--navy)/0.03)]" data-testid={`inbox-thread-${t.id}`}>
            <button type="button" onClick={() => setOpen(on && thread.length > 1 ? null : t.id)} className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left">
              <span className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--navy))] text-white"><Reply className="h-3.5 w-3.5" /></span>
                <span className="min-w-0 leading-tight">
                  <span className="block truncate text-[13px] font-semibold">{t.by_name} svarte · {t.subject}</span>
                  <span className="block truncate text-[11.5px] text-muted-foreground">Til {t.to}{t.cc?.length ? ` · kopi ${t.cc.join(", ")}` : ""} · {fmtDateTime(t.sent_at)}</span>
                </span>
              </span>
              <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", on && "rotate-180")} />
            </button>
            {on ? <div className="whitespace-pre-wrap border-t border-black/[0.05] px-5 py-4 text-[14px] leading-[1.7] text-foreground/90">{t.body}</div> : null}
          </article>
        );
      })}
    </section>
  );
}

/** Inline reply composer (Superhuman-style): To/Cc/Subject lines, body, KI draft, send. */
export function ReplyComposer({ p, open, onClose, onSent }) {
  const { user } = useAuth();
  const em = p.email || {};
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [fromAi, setFromAi] = useState(false);
  const bodyRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTo(p.contact?.email || "");
    setCc((em.cc || []).join(", "));
    setSubject((s) => s || (em.subject ? (em.subject.startsWith("Re:") ? em.subject : `Re: ${em.subject}`) : `Re: ${p.name || "Deres forespørsel"}`));
    setTimeout(() => { rootRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); bodyRef.current?.focus(); }, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, p.id]);

  useEffect(() => {
    const h = (e) => { const d = e.detail || {}; if (d.subject) setSubject(d.subject); if (d.body) { setBody(d.body); setFromAi(true); } setTimeout(() => bodyRef.current?.focus(), 50); };
    window.addEventListener("assistant:draft", h);
    return () => window.removeEventListener("assistant:draft", h);
  }, []);

  if (!open) return null;

  const draft = async () => {
    setDrafting(true);
    try {
      const r = await api.post(`/inbox/${p.id}/draft`, { instructions: body.trim() && !fromAi ? `Bygg videre på disse notatene: ${body.trim().slice(0, 600)}` : "" });
      setSubject(r.data.subject || subject);
      setBody(r.data.body || "");
      setFromAi(true);
      bodyRef.current?.focus();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDrafting(false);
    }
  };
  const send = async () => {
    if (!to.trim() || !body.trim()) return toast.error("Mottaker og tekst må fylles ut");
    setSending(true);
    try {
      const r = await api.post(`/inbox/${p.id}/reply`, { to: to.trim(), cc: cc.split(",").map((x) => x.trim()).filter(Boolean), subject: subject.trim(), body: body.trim() });
      onSent(r.data.thread);
      setBody(""); setFromAi(false);
      toast.success("Svaret er registrert på saken", { description: "E-postutsending er ikke koblet til ennå – svaret ligger i tråden." });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <section ref={rootRef} className="composer-shadow overflow-hidden rounded-[22px] border border-black/[0.08] bg-white fade-up" data-testid="inbox-reply-composer">
      <div className="flex items-center justify-between border-b border-black/[0.05] px-5 py-3">
        <div className="text-[13px] font-semibold">Svar til {p.contact?.name || p.customer?.name}</div>
        <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" aria-label="Lukk" data-testid="inbox-reply-close"><X className="h-4 w-4" /></button>
      </div>
      <Line label="Til" testId="inbox-reply-to-line"><input value={to} onChange={(e) => setTo(e.target.value)} className={field} placeholder="navn@verft.no" data-testid="inbox-reply-to" /></Line>
      <Line label="Kopi" testId="inbox-reply-cc-line"><input value={cc} onChange={(e) => setCc(e.target.value)} className={field} placeholder="valgfritt – skill med komma" data-testid="inbox-reply-cc" /></Line>
      <Line label="Emne" testId="inbox-reply-subject-line"><input value={subject} onChange={(e) => setSubject(e.target.value)} className={cn(field, "font-medium")} data-testid="inbox-reply-subject" /></Line>
      <div className="relative">
        <textarea ref={bodyRef} value={body} onChange={(e) => { setBody(e.target.value); setFromAi(false); }} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); send(); } }} rows={Math.min(18, Math.max(7, body.split("\n").length + 2))} placeholder={`Skriv svaret – eller la KI lage et utkast basert på forespørselen og det vi har lest.\n\n${user?.name}\n${user?.title || ""}\nLOS Marine AS · Rubbestadneset`} className="caret-brand block w-full resize-none bg-transparent px-5 py-4 text-[14.5px] leading-[1.7] outline-none placeholder:text-muted-foreground/45" data-testid="inbox-reply-body" />
        {drafting ? <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[1px]" data-testid="inbox-reply-drafting"><span className="inline-flex items-center gap-2.5 rounded-full bg-white px-4 py-2 text-[13px] font-medium shadow-float"><span className="pulse-dot h-2.5 w-2.5 rounded-full bg-primary" />Leser forespørselen og skriver utkast…</span></div> : null}
      </div>
      {fromAi ? <div className="mx-5 mb-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-soft))] px-3 py-1 text-[11.5px] font-medium text-[hsl(208_90%_34%)]" data-testid="inbox-reply-ai-note"><Sparkles className="h-3 w-3" /> KI-utkast – les gjennom før du sender</div> : null}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-black/[0.05] px-4 py-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={draft} disabled={drafting || sending} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-3.5 text-[12.5px] font-semibold text-[hsl(var(--navy))] transition-colors hover:border-black/[0.2] disabled:opacity-50" data-testid="inbox-reply-draft"><Sparkles className="h-4 w-4 text-primary" /> {body.trim() && !fromAi ? "Forbedre med KI" : "Skriv utkast med KI"}</button>
          <span className="hidden items-center gap-1 text-[11.5px] text-muted-foreground sm:inline-flex"><Info className="h-3 w-3" /> Svaret lagres på saken og i canvaset</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="kbd hidden sm:inline-flex">⌘ ↵</span>
          <button type="button" onClick={send} disabled={sending || drafting} className="inline-flex h-10 items-center gap-2 rounded-full bg-[hsl(var(--navy))] px-4 text-[13.5px] font-semibold text-white transition-[background-color,transform] hover:bg-[hsl(var(--ink-2))] active:scale-[0.98] disabled:opacity-50" data-testid="inbox-reply-send">{sending ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <Send className="h-4 w-4" />} Send svar</button>
        </div>
      </div>
    </section>
  );
}
